import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid'
import type { Announcement } from '../../types/announcement'
import { isUnread, needsAcknowledgement } from '../../types/announcement'
import { formatRelativeTime } from './announcementFormat'
import AnnouncementTypeBadge from './AnnouncementTypeBadge'

interface Props {
  announcement: Announcement
  onOpen: (announcement: Announcement) => void
}

// One inline row (feed + dashboard widget). Shows a line-clamped body preview;
// the full body lives in the modal opened on click. The ack marker here is a
// status indicator only — acknowledging happens inside the modal after reading.
const AnnouncementRow = ({ announcement, onOpen }: Props) => {
  const unread = isUnread(announcement)
  const ackPending = needsAcknowledgement(announcement)
  const acked = !!announcement.myReceipt?.acknowledged_at
  const author = announcement.author?.name
  const when = formatRelativeTime(announcement.published_at ?? announcement.created_at)

  return (
    <button
      type="button"
      onClick={() => onOpen(announcement)}
      className="w-full text-left flex gap-3 px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg"
    >
      {/* Unread dot — keeps the column aligned whether or not it's shown */}
      <span className="flex-shrink-0 mt-1.5 w-2.5">
        {unread && <span className="block h-2.5 w-2.5 rounded-full bg-primary-500" aria-label="Unread" />}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <AnnouncementTypeBadge type={announcement.type} />
          {ackPending && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
              <ExclamationCircleIcon className="h-4 w-4" />
              Needs acknowledgement
            </span>
          )}
          {acked && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
              <CheckCircleIcon className="h-4 w-4" />
              Acknowledged
            </span>
          )}
        </div>

        <p className={`mt-1 text-sm truncate ${unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
          {announcement.title}
        </p>

        {/* Line-clamped preview of the (possibly long) body */}
        <p className="mt-0.5 text-sm text-gray-500 line-clamp-2 whitespace-pre-line">
          {announcement.body}
        </p>

        <p className="mt-1 text-xs text-gray-400">
          {author ? `${author} · ` : ''}{when}
        </p>
      </div>
    </button>
  )
}

export default AnnouncementRow
